// TelcoTrack — mustr textu „Prázdná smlouva“ (Dokumenty / digitální smlouva).

/** Hlavička strany: fyzická osoba vs firma. */
export type BlankContractPartyKind = 'person' | 'company';

export interface BlankContractBodyInput {
  /** Název smlouvy / akce. */
  title: string;
  /** Místo (obec / lokalita). */
  place: string;
  /** Datum smlouvy YYYY-MM-DD. */
  contractDate: string;
}

export interface BlankContractPartyInput {
  kind: BlankContractPartyKind;
  /** FO: jméno osoby. Firma: obchodní jméno. */
  fullName: string;
  /** Firma: IČO (8 číslic). */
  ico?: string | null;
  /** Adresa (FO bydliště / firma sídlo). */
  address?: string | null;
  /** DIČ (volitelné, z ARES). */
  dic?: string | null;
  /** Zástupce k podpisu (u FO často totéž jako fullName). */
  signatoryName?: string | null;
  /** Role / funkce zástupce. */
  signatoryRole?: string | null;
}

/** Formát data pro právní text (dd.mm.yyyy). */
export function formatBlankContractLegalDate(isoDate: string): string {
  const trimmed = isoDate.trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (m) return `${m[3]}.${m[2]}.${m[1]}`;
  const parsed = new Date(trimmed);
  if (!Number.isFinite(parsed.getTime())) return trimmed || '—';
  return new Intl.DateTimeFormat('cs-CZ', {
    timeZone: 'Europe/Prague',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(parsed);
}

/** Normalizace času HH:mm (přijme i H:m / H:mm). */
export function normalizeBlankContractTime(raw: string): string {
  const trimmed = (raw ?? '').trim();
  const m = /^(\d{1,2}):(\d{1,2})$/.exec(trimmed);
  if (!m) return trimmed;
  const h = Math.min(23, Math.max(0, Number(m[1])));
  const min = Math.min(59, Math.max(0, Number(m[2])));
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/** Aktuální čas Europe/Prague jako HH:mm. */
export function currentBlankContractTimePrague(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Prague',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);
  const hour = parts.find((p) => p.type === 'hour')?.value ?? '00';
  const minute = parts.find((p) => p.type === 'minute')?.value ?? '00';
  return normalizeBlankContractTime(`${hour}:${minute}`);
}

/** Aktuální datum Europe/Prague jako YYYY-MM-DD. */
export function currentBlankContractDatePrague(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Prague',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const y = parts.find((p) => p.type === 'year')?.value ?? '1970';
  const m = parts.find((p) => p.type === 'month')?.value ?? '01';
  const d = parts.find((p) => p.type === 'day')?.value ?? '01';
  return `${y}-${m}-${d}`;
}

/** IČO → jen číslice, pad na 8 (ARES). */
export function normalizeBlankContractIco(raw: string): string {
  const digits = (raw ?? '').replace(/\D/g, '');
  if (!digits) return '';
  return digits.padStart(8, '0').slice(-8);
}

/** „Jméno“ nebo „Jméno, role“. */
export function formatBlankContractPartyLabel(name: string, role?: string | null): string {
  const n = name.trim();
  const r = (role ?? '').trim();
  if (!n) return r;
  if (!r) return n;
  return `${n}, ${r}`;
}

/** Jednořádkový popis strany pro metadata / shrnutí. */
export function formatBlankContractPartySummary(party: BlankContractPartyInput): string {
  const kind = party.kind === 'company' ? 'firma' : 'FO';
  const name = (party.fullName ?? '').trim() || '—';
  const ico = normalizeBlankContractIco(party.ico ?? '');
  const sig = (party.signatoryName ?? '').trim();
  const role = (party.signatoryRole ?? '').trim();
  const zastupce = formatBlankContractPartyLabel(sig, role);
  const parts = [`[${kind}] ${name}`];
  if (ico) parts.push(`IČO ${ico}`);
  if (zastupce) parts.push(`zástupce: ${zastupce}`);
  return parts.join(' · ');
}

/**
 * Sestaví editovatelný úvodní text těla „Prázdné smlouvy“.
 * Uživatel může text libovolně upravit / rozšířit.
 */
export function buildBlankContractBodyText(input: BlankContractBodyInput): string {
  const title = input.title.trim() || 'smlouva';
  const place = input.place.trim() || '…';
  const date = formatBlankContractLegalDate(input.contractDate);

  return [
    `Smluvní strany uzavírají tuto ${title} níže uvedeného dne.`,
    '',
    `Místo: ${place}`,
    `Datum: ${date}`,
    '',
    'Předmět a ujednání:',
    '…',
    '',
    'Smluvní strany prohlašují, že text smlouvy si přečetly, s jeho obsahem souhlasí a na důkaz toho připojují své podpisy.',
  ].join('\n');
}
